/**
 * Supabase client — singleton, used throughout the app.
 *
 * Configured at build time from VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.
 * Service-role key only used in /scripts (never in client).
 */
import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Supabase credentials missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local'
  );
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

// ============================================================================
// VEHICLE QUERIES
// ============================================================================
export async function getVehiclesForMarket(marketCode) {
  const { data, error } = await supabase
    .from('vehicles_trip_ready')
    .select('*')
    .eq('market_code', marketCode)
    .order('brand', { ascending: true })
    .order('model', { ascending: true });
  if (error) throw error;
  return data;
}

export async function getVehicleById(vehicleId) {
  const { data, error } = await supabase
    .from('vehicles')
    .select('*')
    .eq('vehicle_id', vehicleId)
    .single();
  if (error) throw error;
  return data;
}

// ============================================================================
// STATION QUERIES
// ============================================================================
export async function getStationsInBoundingBox(bbox, options = {}) {
  // bbox = { minLat, minLng, maxLat, maxLng }
  // options = { connectorTypes: [...], minPower: 50, status: 'operational' }
  let q = supabase
    .from('stations')
    .select('*')
    .gte('latitude', bbox.minLat)
    .lte('latitude', bbox.maxLat)
    .gte('longitude', bbox.minLng)
    .lte('longitude', bbox.maxLng)
    .eq('status', options.status ?? 'operational');

  if (options.minPower) {
    q = q.gte('peak_dc_kw', options.minPower);
  }

  if (options.connectorTypes) {
    q = q.overlaps('dc_connectors', options.connectorTypes);
  }

  const { data, error } = await q;
  if (error) throw error;
  return data;
}

// ============================================================================
// NETWORK / TARIFF QUERIES
// ============================================================================
export async function getNetworksForMarket(marketCode) {
  const { data, error } = await supabase
    .from('networks')
    .select('*')
    .eq('market_code', marketCode);
  if (error) throw error;
  return data;
}

// ============================================================================
// TRIP CREATE / READ
// ============================================================================
export async function createTrip(trip) {
  const { data, error } = await supabase
    .from('trips')
    .insert(trip)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getUserTrips(userId) {
  const { data, error } = await supabase
    .from('trips')
    .select('*')
    .eq('user_id', userId)
    .order('planned_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return data;
}
